/**
 * Authentication state.
 *
 * The flow is shaped by one fact: every WhatsApp OTP costs money.
 *
 *   First time on a device:  OTP  → verify → set PIN
 *   Every day after:         PIN  (free)
 *   Token near expiry:       silent refresh (free)
 *   New or wiped device:     OTP again
 *
 * A salesperson therefore triggers roughly four OTPs a year instead of one per
 * working day.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { api, ApiError, deviceFingerprint, getSession, loadSession, onAuthChange, setSession } from './api';
/**
 * Static, including kvGet and kvSet.
 *
 * Those two were pulled in with `await import` at four call sites below,
 * which achieved nothing: this module already imports './idb' statically on
 * this line, so idb was in the same chunk either way. Vite reports the same
 * pattern for lib/camera in Ledger.tsx — a dynamic import of a module that is
 * also statically imported "will not move module into another chunk".
 *
 * The only real effect was four extra awaits on the login path, where
 * recallPhone runs before the phone field is populated.
 */
import { wipeUserData, kvGet, kvSet } from './idb';
import type { Locale } from './i18n';

export type RoleCode = 'customer' | 'salesperson' | 'driver' | 'owner_admin' | 'accountant' | 'manager' | 'field_agent' | 'other_staff';

export interface AuthUser {
  id: string;
  fullName: string;
  phone: string;
  role: RoleCode;
  locale: Locale;
  salespersonId: string | null;
  driverId: string | null;
  customerId: string | null;
}

interface AuthValue {
  user: AuthUser | null;
  ready: boolean;
  /** True once a PIN exists, so the app can offer PIN login by default. */
  hasPin: boolean;

  requestOtp: (phone: string, force?: boolean) => Promise<{ isRegistration: boolean; hasPin: boolean; expiresInMinutes: number }>;
  verifyOtp: (phone: string, otp: string) =>
    Promise<{ needsRegistration: boolean; hasPin: boolean; registrationTicket?: string }>;
  pinLogin: (phone: string, pin: string) => Promise<void>;
  setPin: (pin: string) => Promise<void>;
  /**
   * DEV / TEST ONLY. Signs in as a seeded role with no OTP. The server route is
   * fenced to non-production, so in a production build this call just fails; the
   * UI only ever offers it under import.meta.env.DEV.
   */
  devLogin: (role: string) => Promise<void>;
  logout: () => Promise<void>;

  /** Convenience checks used throughout the UI. */
  is: (...roles: RoleCode[]) => boolean;
  isStaff: boolean;
}

const AuthContext = createContext<AuthValue>({
  user: null, ready: false, hasPin: false,
  requestOtp: async () => ({ isRegistration: false, hasPin: false, expiresInMinutes: 10 }),
  verifyOtp: async () => ({ needsRegistration: false, hasPin: false }),
  pinLogin: async () => {}, setPin: async () => {}, devLogin: async () => {}, logout: async () => {},
  is: () => false, isStaff: false,
});

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  hasPin?: boolean;
  user: AuthUser;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);
  const [hasPin, setHasPin] = useState(false);

  // Restore the session from IndexedDB on boot, so a returning user lands on
  // their home screen rather than a login form.
  useEffect(() => {
    void (async () => {
      const session = await loadSession();
      setUser((session?.user as AuthUser) ?? null);
      setHasPin(Boolean(await hasStoredPinFlag()));
      setReady(true);
    })();

    return onAuthChange((s) => setUser((s?.user as AuthUser) ?? null));
  }, []);

  const persist = useCallback(async (res: TokenResponse) => {
    const fp = await deviceFingerprint();
    await setSession({
      accessToken: res.accessToken,
      refreshToken: res.refreshToken,
      // Refresh a minute early so a request never races the expiry.
      expiresAt: Date.now() + (res.expiresIn - 60) * 1000,
      user: res.user,
      deviceFingerprint: fp,
    });
    setUser(res.user);
    if (res.hasPin !== undefined) {
      setHasPin(res.hasPin);
      await setStoredPinFlag(res.hasPin);
    }
  }, []);

  /**
   * `force` skips the server's own 24-hour-window shortcut and asks Meta
   * directly. Used by the "I have already sent it" path: our window record
   * comes from a webhook, and a webhook can be silent for reasons that are
   * ours rather than the user's.
   */
  const requestOtp = useCallback(async (phone: string, force = false) => {
    const data = await api.post<{
      sent: boolean; phone: string; expiresInMinutes: number;
      isRegistration: boolean; hasPin: boolean;
    }>('/api/auth/request-otp', { phone, ...(force ? { force: true } : {}) }, { anonymous: true });
    return {
      isRegistration: data.isRegistration,
      hasPin: data.hasPin,
      expiresInMinutes: data.expiresInMinutes,
    };
  }, []);

  const verifyOtp = useCallback(async (phone: string, otp: string) => {
    const fp = await deviceFingerprint();
    const data = await api.post<
      | { verified: true; needsRegistration: true; phone: string; message: string;
          registrationTicket: string }
      | (TokenResponse & { hasPin: boolean })
    >('/api/auth/verify-otp', {
      phone,
      otp,
      deviceFingerprint: fp,
      deviceModel: navigator.userAgent.slice(0, 100),
      appVersion: __APP_VERSION__,
      // One PWA serves every role, so the bundle is reported as 'staff'.
      appBundle: 'staff',
    }, { anonymous: true });

    if ('needsRegistration' in data && data.needsRegistration) {
      /**
       * The ticket travels with it. Without this the registration form has
       * no proof the number was verified, and POST /api/customers/register
       * refuses it — which is what every self-registration did until 7 Oct,
       * silently, after the whole form had been filled in.
       */
      return {
        needsRegistration: true,
        hasPin: false,
        registrationTicket: data.registrationTicket,
      };
    }

    await persist(data as TokenResponse & { hasPin: boolean });
    return { needsRegistration: false, hasPin: (data as { hasPin: boolean }).hasPin };
  }, [persist]);

  const pinLogin = useCallback(async (phone: string, pin: string) => {
    const fp = await deviceFingerprint();
    const data = await api.post<TokenResponse & { hasPin: boolean }>(
      '/api/auth/pin-login',
      { phone, pin, deviceFingerprint: fp },
      { anonymous: true },
    );
    await persist(data);
  }, [persist]);

  const setPin = useCallback(async (pin: string) => {
    await api.post('/api/auth/set-pin', { pin });
    setHasPin(true);
    await setStoredPinFlag(true);
  }, []);

  // DEV / TEST ONLY — see the type. The server route 404s in production, so this
  // throws there; the UI only exposes it under import.meta.env.DEV.
  const devLogin = useCallback(async (role: string) => {
    const data = await api.post<TokenResponse & { hasPin: boolean }>(
      '/api/auth/dev-login', { role }, { anonymous: true },
    );
    await persist(data);
  }, [persist]);

  const logout = useCallback(async () => {
    const session = getSession();
    try {
      if (session?.refreshToken) {
        await api.post('/api/auth/logout', { refreshToken: session.refreshToken });
      }
    } catch {
      // Signing out must always succeed locally, even with no network.
    }
    await setSession(null);
    // Everything cached on the device goes: a shared phone must not leave one
    // person's customer list visible to the next.
    await wipeUserData();
    setUser(null);
    setHasPin(false);
  }, []);

  const value = useMemo<AuthValue>(() => ({
    user,
    ready,
    hasPin,
    requestOtp,
    verifyOtp,
    pinLogin,
    setPin,
    devLogin,
    logout,
    is: (...roles: RoleCode[]) => !!user && roles.includes(user.role),
    isStaff: !!user && user.role !== 'customer',
  }), [user, ready, hasPin, requestOtp, verifyOtp, pinLogin, setPin, devLogin, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  return useContext(AuthContext);
}

// ---------------------------------------------------------------------------
// "A PIN exists" flag
//
// Only a hint for the UI — it decides whether to show the PIN screen or the
// OTP screen first. The server is always the authority; a wrong guess here
// costs one extra tap, never access.
// ---------------------------------------------------------------------------

async function hasStoredPinFlag(): Promise<boolean> {
  return (await kvGet<boolean>('hasPin')) ?? false;
}

async function setStoredPinFlag(value: boolean): Promise<void> {
  await kvSet('hasPin', value);
}

/** Last phone number used, so a returning user does not retype it. */
export async function rememberPhone(phone: string): Promise<void> {
  await kvSet('lastPhone', phone);
}

export async function recallPhone(): Promise<string> {
  return (await kvGet<string>('lastPhone')) ?? '';
}

/**
 * Which route a role lands on after signing in.
 *
 * Each role's first screen is the thing they open the app to do: a retailer to
 * order, a salesperson to start their day, a driver to see the route, an admin
 * to clear approvals.
 */
export function homeRouteFor(role: RoleCode): string {
  switch (role) {
    case 'customer': return '/home';
    case 'salesperson': return '/day';
    // My Day, not the route. A driver cannot open a route until his day is
    // started — the vehicle, location and selfie are captured there — so
    // landing him on the route showed him an empty screen with no way forward.
    case 'driver': return '/day';
    // The combined field agent runs a driver-style day (vehicle + van), so they
    // land on My Day just like a driver.
    case 'field_agent': return '/day';
    // Other staff (warehouse / loaders / general hands) run no field day and hold
    // no operational screens — they sign in to see their own details and to
    // message the office — so their home IS their profile.
    case 'other_staff': return '/profile';
    case 'owner_admin':
    case 'manager':
    case 'accountant': return '/dashboard';
    default: return '/home';
  }
}

/** Human-readable message for a login failure. */
export function authErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Something went wrong. Please try again.';
  switch (err.code) {
    case 'INVALID_OTP': return 'That code is not correct.';
    case 'OTP_EXPIRED': return 'That code has expired. Request a new one.';
    case 'OTP_RATE_LIMITED': return err.message;
    case 'INVALID_PIN': return err.message;
    case 'PIN_LOCKED': return err.message;
    case 'DEVICE_NOT_REGISTERED': return 'This device needs verification. Sign in with a code.';
    // An administrator signed this device out. Said plainly, because the
    // alternative — "session expired" — sends the user round the login loop
    // trying to fix something that is not theirs to fix.
    case 'DEVICE_REVOKED':
      return 'This device was signed out by GD Strategic. Please contact the office.';
    case 'WHATSAPP_WINDOW_CLOSED': return err.message;
    // Shown when the WhatsApp token has lapsed or the account is suspended —
    // an outage on our side, not the user's fault. A retailer standing in a
    // shop cannot act on "WhatsApp messaging is not configured", and telling
    // them to keep trying wastes their time.
    case 'WHATSAPP_NOT_CONFIGURED':
      return 'Sign-in by code is temporarily unavailable. Please call GD Strategic.';
    case 'PENDING_APPROVAL': return 'Your account is awaiting approval.';
    case 'ACCOUNT_REJECTED': return err.message;
    case 'ACCOUNT_SUSPENDED': return 'This account is suspended. Please contact us.';
    case 'NETWORK_ERROR': return err.message;
    default: return err.message;
  }
}
